import { JSX, createContext, useContext, useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import ReactGA from 'react-ga4';
import { useLocation } from 'react-router-dom';
import AppShell from './AppShell';

type AppShellContextType = {
  headerLeft: JSX.Element | null;
  setHeaderLeft: React.Dispatch<React.SetStateAction<JSX.Element | null>>;
  isInIframe: boolean;
};
const initialValue: AppShellContextType = { headerLeft: null, setHeaderLeft: () => {}, isInIframe: false };
const AppShellContext = createContext<AppShellContextType>(initialValue);
const AppShellProvider = ({ children }: { children?: any }) => {
  const [headerLeft, setHeaderLeft] = useState<JSX.Element | null>(null);
  const [isInIframe, setIsInIFrame] = useState<boolean>(false);

  /**
   * Detects if the current window is embedded in an iframe.
   * Handles both same-origin and cross-origin iframe scenarios.
   * @returns true if the window is in an iframe, false otherwise
   */
  const isInIframeHelper = (): boolean => {
    try {
      // Primary method: Check if window.self !== window.top
      // This works for same-origin and cross-origin iframes
      return window.self !== window.top;
    } catch (e) {
      // Cross-origin iframe will throw a security error
      // If we catch this, it means we're in a cross-origin iframe
      return true;
    }
  };

  useEffect(() => {
    // Detect if we're in an iframe - check this immediately on mount
    setIsInIFrame(isInIframeHelper());
  }, []);

  return (
    <AppShellContext.Provider value={{ headerLeft, setHeaderLeft, isInIframe }}>
      <AppShell />
      <RouteTracker />
    </AppShellContext.Provider>
  );
};

export default AppShellProvider;
export const useAppShellContext = () => useContext(AppShellContext);

const RouteTracker = () => {
  const location = useLocation();
  const [cookies] = useCookies(['consent']);

  const sendMinimalPageView = async (path: string, isMinimal: boolean) => {
    ReactGA.send({
      hitType: 'pageview',
      page: path,
      anonymizeIp: isMinimal // Ensures IP is anonymized
    });
  };
  useEffect(() => {
    const userConsent = cookies['consent'];
    if (userConsent === 'accepted') {
      sendMinimalPageView(location.pathname + location.search, false);
    } else {
      sendMinimalPageView(location.pathname + location.search, true);
    }
  }, [cookies, location]);

  return null;
};
